import { ActionContainer } from "@/components/layout/ActionContainer";
import styled from "@emotion/styled";
import { createInstallationQuotation } from "@repo/api/src/installationQuotation/installationQuotation";
import { useInstallAvailability } from "@repo/api/src/salesforce/installAvailability";
import {
  Button,
  FormField,
  InlineMessage,
  Input,
  Variables,
  linkOutIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { useToast } from "@repo/ui";
import { getErrorMessage } from "@repo/utils";
import { useEffect, useRef, useState } from "react";

interface InstallationServiceCheckerProps {
  projectId: number;
  customerPostalCode?: string;
  addressDataMissing?: boolean;
}

const MessageContainer = styled.div`
  margin-top: ${Variables.space100};
`;

export const InstallationServiceChecker = ({
  projectId,
  customerPostalCode,
  addressDataMissing,
}: InstallationServiceCheckerProps) => {
  const [zipCode, setZipCode] = useState("");
  const [validZipCode, setValidZipCode] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [message, setMessage] = useState("");
  const [validationType, setValidationType] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { updateToast } = useToast();
  const { data: isInstallationServiceAvailable, isValidating } =
    useInstallAvailability(validZipCode);
  const postalCodeRegex = /^\d*$/;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (customerPostalCode) {
      setZipCode(customerPostalCode);
      setValidZipCode(customerPostalCode);
    }
  }, [customerPostalCode]);

  useEffect(() => {
    if (isInstallationServiceAvailable === true) {
      setIsValid(true);
      setValidationType("success");
      setMessage(t("prompt.available_for_installation_services"));
    } else if (isInstallationServiceAvailable === false) {
      setIsValid(false);
      setValidationType("error");
      setMessage(t("prompt.not_available"));
    }
  }, [isInstallationServiceAvailable]);

  function handleZipChange(zip: string) {
    setZipCode(zip);
    setValidZipCode("");
    evaluateValidity(zip);
  }

  function evaluateValidity(zip: string) {
    if ((zip.length > 0 && zip.length !== 5) || !postalCodeRegex.test(zip)) {
      setIsValid(false);
      setValidationType("error");
      setMessage(t("prompt.invalid_postal_code"));
    } else {
      setIsValid(true);
      setValidZipCode(zip);
      setValidationType("");
      setMessage("");
    }
  }

  function handleCheckAnotherZipCode() {
    inputRef.current?.focus();
    handleZipChange("");
  }

  async function handleCreateInstallationQuotation() {
    setIsSaving(true);
    try {
      await createInstallationQuotation({ projectId });
      updateToast({ title: t("prompt.installation_quotation_saved") });
    } catch (err) {
      updateToast({
        title: t("prompt.installation_quotation_saved_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <FormField
        disabled={isValidating || isSaving}
        shouldValidate={!isValidating && Boolean(zipCode)}
        valid={isValid}
        validation={{ msg: message, type: !isValidating ? validationType : "" }}
        fieldHelper={{
          msg: !isValidating ? message : "",
          type:
            !isValidating && isInstallationServiceAvailable ? "success" : "",
        }}
      >
        <Input
          ref={inputRef}
          id="installation-service-checker-zip-code"
          label={t("label.zip_code")}
          type="text"
          value={zipCode}
          disabled={isValidating || isSaving}
          onChange={(e) => handleZipChange(e.target.value)}
        />
      </FormField>
      <ActionContainer>
        <Button
          size="small"
          text={t("label.check_another_zip_code")}
          onClick={handleCheckAnotherZipCode}
          disabled={isSaving}
        />
        {!isValidating && isInstallationServiceAvailable && (
          <Button
            size="small"
            text={t("label.create_installation_quotation")}
            iconPosition="trailing"
            ssrIcon={linkOutIcon}
            onClick={handleCreateInstallationQuotation}
            loading={isSaving}
          />
        )}
      </ActionContainer>
      {!isValidating &&
        isInstallationServiceAvailable &&
        addressDataMissing && (
          <MessageContainer>
            <InlineMessage
              title={t("prompt.customer_address_data_missing")}
              body={t("prompt.customer_address_data_missing_desc")}
              variant="cautionary"
              subtle={true}
            />
          </MessageContainer>
        )}
    </>
  );
};
